import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import update from 'immutability-helper';
import Alert from '@material-ui/lab/Alert';
import Loader from '../../../components/Loader';
import { ProviderAPI } from '../../../api';
import { TaxonomyStore } from '../../../store';
import {
  taxonomyTreeToIdArray,
  taxonomyListToIdArray,
  taxonomyItemId,
} from '../../../lib/util.js';
import equal from 'fast-deep-equal/es6/react';
import { dataDiff } from '../../../lib/util';
import ListingEditForm from './ListingEditForm';
import moment from 'moment';
import TaskComplete from '../../../components/dialogues/TaskComplete';
import { uploadListingImage } from '../../../api/s3';

const DEFAULT_ERROR_MESSAGE = 'Listing creation failed. Check you have entered all required sections including the Name and Service.';

const multiSelectFields = [
  'service_locations',
  'access_methods',
  'age_groups',
];

const EditListing = ({providerUuid, listingUuid=null, back}) => {
  const [busy, setBusy] = React.useState(false);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertState, setAlterState] = React.useState(null);
  const [alertCaption, setAlterCaption] = React.useState('');
  const [initial, setInitial] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [completed, setCompleted] = React.useState(false);
  const [imageFile, setImageFile] = React.useState(null);
  const serviceTaxonomy = useSelector(state => state.taxonomy.services);
  const dispatch = useDispatch();

  const getDataForUpdate = async () => {
    try {
      const { payload } = await ProviderAPI.getListing(listingUuid);
      const initialised = {};
      Object.keys(payload).forEach(k => {
        if (!Array.isArray(payload[k])) {
          initialised[k] = payload[k];
        }
      });
      initialised['service'] = taxonomyItemId(payload.service, serviceTaxonomy);
      initialised['service_locations'] = taxonomyTreeToIdArray(payload.service_locations);
      initialised['access_methods'] = taxonomyListToIdArray(payload.access_methods);
      initialised['age_groups'] = taxonomyListToIdArray(payload.age_groups);
      setInitial(initialised);
      setData(initialised);
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  };

  const getDataForCreate = () => {
    const initialData = {
      title: '',
      short_description: '',
      full_description: '',
      active_from: moment().format(),
      active_to: moment().add(90, 'days').format(),
      minimum_session_time: 'no minimum',
      minimum_weekly_time: 'no minimum',
      units_available: null,
      service: null,
      status: 'draft',
      service_locations: [],
      age_groups: [],
      access_methods: [],
    };
    setData(initialData);
  };

  const triggerAlert = (caption, variant) => {
    setAlterCaption(caption);
    setAlterState(variant);
    setAlertOpen(true);
  };

  const closeAlert = () => {
    setAlertOpen(false);
  };

  const clearAllLocations = () => {
    const updatedData = update(data, {service_locations: {$set: []}});
    setData(updatedData);
  };

  const copyProviderLocations = async () => {
    // This would be better implemented using a profile in store but time
    // requires a quick solution
    try {
      const { payload } = await ProviderAPI.getProfile(providerUuid);
      const providerLocations = taxonomyTreeToIdArray(payload.service_locations);
      const updatedData = update(data, {service_locations: {$set: providerLocations}});
      setData(updatedData);
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  };

  const handleChange = (value, field) => {
    // const value = (event.target === undefined) ? event : event.target.value;
    let updatedData;
    if (multiSelectFields.includes(field)) {
      const intValue = parseInt(value);
      const currentArr = data[field];
      let newArr;
      if (currentArr.includes(intValue)) {
        newArr = currentArr.filter(item => item !== intValue);
      } else {
        newArr = currentArr.filter(item => item); // must deep copy
        newArr.push(intValue);
      }
      updatedData = update(data, {[field]: {$set: newArr}});
    } else if (field === 'service') {
      updatedData = update(data, {[field]: {$set: parseInt(value)}});
    } else {
      updatedData = update(data, {[field]: {$set: value}});
    }
    setData(updatedData);
  };

  const saveUpdate = async () => {
    const patchData = dataDiff(initial, data);
    try {
      await ProviderAPI.updateListing(providerUuid, listingUuid, patchData);
      triggerAlert('Listing successfully updated.', 'success');
      setInitial(data);
      setBusy(false);
    } catch (err) {
      const defaultMessage = 'Update failed. Check you have completed all sections.';
      const message = err.body && err.body.message ? err.body.message : defaultMessage;
      triggerAlert(message, 'warning');
      setBusy(false);
    }
  };

  const saveCreate = async () => {
    try {
      const { payload } = await ProviderAPI.createListing(providerUuid, data);
      const listingUuid = payload.uuid;
      setCompleted(true);
      return listingUuid;
    } catch (err) {
      const msg = err.body && err.body.message ? err.body.message : DEFAULT_ERROR_MESSAGE;
      triggerAlert(msg, 'warning');
      setBusy(false);
    }
  };

  const save = async () => {
    const dataChanged = !equal(initial, data);
    if (!dataChanged && !imageFile) {
      triggerAlert('You haven\'t changed anything!', 'info');
      return null;
    }
    setBusy(true);
    let thisListingUuid = listingUuid;
    if (dataChanged) {
      if (initial === null) {
        thisListingUuid = await saveCreate();
      } else {
        await saveUpdate();
      }
    }
    if (imageFile && thisListingUuid) {
      await uploadListingImage(providerUuid, thisListingUuid, imageFile);
      setImageFile();
    }
    setBusy(false);
  };

  React.useEffect(() => {
    // make sure all required taxonomies are loaded
    dispatch(TaxonomyStore.refreshTaxonomy('locations'));
    dispatch(TaxonomyStore.refreshTaxonomy('services'));
    dispatch(TaxonomyStore.refreshTaxonomy('accessMethods'));
    dispatch(TaxonomyStore.refreshTaxonomy('ageGroups'));
  }, [dispatch]);

  React.useEffect(() => {
    if (listingUuid === null) {
      getDataForCreate();
    } else {
      getDataForUpdate();
    }
  }, [serviceTaxonomy, listingUuid]);  // eslint-disable-line

  if (data === null) {
    return (<Loader />);
  }

  const completionMessage = `Your new Listing titled '${data.title}' has been created. It won't be visible on the platform until you set the listing status to 'active'.`;

  return (
    <ListingEditForm
      listing={data}
      handleChange={handleChange}
      clearAllLocations={clearAllLocations}
      copyProviderLocations={copyProviderLocations}
      busy={busy}
      back={back}
      save={save}
      setImageFile={setImageFile}
    >
      {alertOpen ? (
        <Alert severity={alertState} onClose={closeAlert}>
          {alertCaption}
        </Alert>
      ) : null}
      <TaskComplete
        open={completed}
        next={back}
        title="New Listing created"
        message={completionMessage}
      />
    </ListingEditForm>
  );
};

EditListing.propTypes = {
  providerUuid: PropTypes.string.isRequired,
  listingUuid: PropTypes.string,
  back: PropTypes.func.isRequired,
};

export default EditListing;
